import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import { Modal, ModalBody } from 'reactstrap'
import { Bounce, toast } from 'react-toastify'
import Select from 'react-select'
import CustomButton from '../../components/OnboardingComponents/CustomButton'
import CustomInput from '../../components/OnboardingComponents/CustomInput'
import CategoriesMultiselect from '../../components/OnboardingComponents/CategoriesMultiselect'
import ProjectSelect from '../../components/OnboardingComponents/ProjectSelect'
import {
  ChevronDropdown,
  customStyles,
} from '../../components/OnboardingComponents/RoleSelect'
import { ICONS } from '../../constants/icons'
import useCategories from '../../hooks/useCategories'
import { get, patch, post } from '../../api/axios'
import endpoints from '../../api/endpoints'
import { toastApiError } from '../../helpers/apiError'
import { setUser } from '../../store/slices/authSlice'
import { formatCityName, getAddressByType } from '../Profile/profileUser'
import {
  getUserCityHubLevel,
  isCityTierAccessible,
} from '../CityHub/cityHubCity'
import { resolveCallImage } from './callMappers'

const DURATION_OPTIONS = [
  { value: '10', label: '10 Days' },
  { value: '20', label: '20 Days' },
  { value: '30', label: '30 Days' },
]

const SCOPE_OPTIONS = [
  { value: 'global', label: 'Global Call' },
  { value: 'specific', label: 'Specific Cities' },
]

const ADDRESS_TYPE_OPTIONS = [
  { value: 'primary', label: 'Primary City' },
  { value: 'secondary', label: 'Secondary City' },
  { value: 'tertiary', label: 'Tertiary City' },
]

const INFORMAL_LIMIT_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
]

const FORMAL_LIMIT_OPTIONS = [
  { value: '100', label: '100' },
  { value: '250', label: '250' },
  { value: '500', label: '500' },
  { value: '1000', label: '1000' },
]

const getLimitOptions = formType =>
  formType === 'formal' ? FORMAL_LIMIT_OPTIONS : INFORMAL_LIMIT_OPTIONS

const TOAST_OPTIONS = {
  position: 'bottom-center',
  theme: 'colored',
  transition: Bounce,
}

const formatFileSize = bytes => {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1).replace(/\.0$/, '')} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`
}

const TITLE_MAX_LENGTH = 120

const INITIAL_VALUES = {
  project: null,
  categories: [],
  title: '',
  details: '',
  duration: '10',
  respondents: ['primary'],
  scope: 'specific',
  cities: ['primary'],
  limit: '',
  coverImage: null,
  existingMediaIds: [],
}

const buildValidationSchema = formType => {
  const limitValues = getLimitOptions(formType).map(option => option.value)

  return yup.object().shape({
    project: yup
      .object()
      .nullable()
      .shape({
        id: yup.string().required(),
        name: yup.string(),
      })
      .required('Project is required'),
    categories: yup
      .array()
      .of(yup.string())
      .min(1, 'Select at least one category')
      .required('Category is required'),
    title: yup
      .string()
      .trim()
      .required('Title is required')
      .min(3, 'Title must be at least 3 characters')
      .max(
        TITLE_MAX_LENGTH,
        `Title must be ${TITLE_MAX_LENGTH} characters or less`
      ),
    details: yup
      .string()
      .trim()
      .required('Details are required')
      .min(10, 'Please provide at least 10 characters'),
    duration: yup.string().required('Duration is required'),
    ...(formType === 'informal'
      ? {
          respondents: yup
            .array()
            .of(yup.string())
            .min(1, 'Select at least one city')
            .required('Respondents are required'),
        }
      : {
          scope: yup
            .string()
            .oneOf(['global', 'specific'])
            .required('Please select Global Call or Specific Cities'),
          cities: yup.array().of(yup.string()).when('scope', {
            is: 'specific',
            then: schema =>
              schema
                .min(1, 'Select at least one city')
                .required('Cities are required'),
            otherwise: schema => schema.notRequired(),
          }),
        }),
    limit: yup
      .string()
      .oneOf(limitValues, 'Select a valid response limit')
      .required('Response limit is required'),
    coverImage: yup.mixed().nullable(),
  })
}

const STEP_COPY = {
  informal: {
    title: 'Create Informal Inbox',
    editTitle: 'Edit Informal Inbox',
    subtitle:
      "Informal Inboxes are posted immediately. Avoid self-promotion and you'll be golden.",
    submitLabel: 'Publish',
    editSubmitLabel: 'Save Changes',
    titlePlaceholder: 'Give this inbox a short title',
    detailsPlaceholder: 'What do you want to ask the local community about?',
  },
  formal: {
    title: 'Create Formal Call for Artists',
    editTitle: 'Edit Formal Call for Artists',
    subtitle: 'Formal Calls for Artists are reviewed before being posted.',
    submitLabel: 'Submit for Review',
    editSubmitLabel: 'Save Changes',
    titlePlaceholder: 'Give this call a short title',
    detailsPlaceholder: 'Tell us about your call for artists...',
  },
}

const getMediaId = media => {
  const candidate = media?.id ?? media?.uuid ?? media?.media_id ?? null
  if (candidate == null || candidate === '') return null
  const asString = String(candidate).trim()
  return asString || null
}

const callToFormValues = (call, formType) => {
  const raw = call?.raw || call || {}
  const project = Array.isArray(raw.projects) ? raw.projects[0] : call?.project
  const addressTypes = (Array.isArray(raw.cities) ? raw.cities : [])
    .map(city => city?.pivot?.address_type)
    .filter(Boolean)
  const duration = String(raw.open_for_days || 10)
  const limit = String(raw.response_limit || '')
  const cities = addressTypes.length > 0 ? addressTypes : ['primary']
  const limitValues = getLimitOptions(formType).map(option => option.value)
  const mediaList = Array.isArray(raw.media)
    ? raw.media
    : Array.isArray(call?.media)
      ? call.media
      : []

  return {
    project: project?.id
      ? { id: project.id, name: project.name || project.label || '' }
      : null,
    categories: (Array.isArray(raw.categories) ? raw.categories : [])
      .map(category => category?.id || category)
      .filter(Boolean),
    title: String(raw.title || '').trim(),
    details: raw.details || '',
    duration: ['10', '20', '30'].includes(duration) ? duration : '10',
    respondents: cities,
    scope: raw.is_global ? 'global' : 'specific',
    cities,
    limit: limitValues.includes(limit) ? limit : '',
    coverImage: getExistingCoverImage(call, raw),
    existingMediaIds: mediaList.map(getMediaId).filter(Boolean),
  }
}

const getExistingCoverImage = (call, raw = {}) => {
  const mediaList = Array.isArray(raw.media)
    ? raw.media
    : Array.isArray(call?.media)
      ? call.media
      : []
  const url = resolveCallImage({ media: mediaList })
  if (!url) return null
  const media = mediaList[0]
  return {
    url,
    name:
      media?.file_name ||
      media?.name ||
      media?.original_name ||
      'Cover image',
    existing: true,
    id: getMediaId(media),
  }
}

const CoverAttachment = ({ coverImage, onRemove }) => {
  const [fileUrl, setFileUrl] = useState(null)

  useEffect(() => {
    if (!(coverImage instanceof File)) {
      setFileUrl(null)
      return undefined
    }
    const url = URL.createObjectURL(coverImage)
    setFileUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [coverImage])

  const previewUrl =
    coverImage instanceof File ? fileUrl : coverImage?.url || null
  const name =
    coverImage instanceof File
      ? coverImage.name
      : coverImage?.name || 'Cover image'
  const sizeLabel =
    coverImage instanceof File ? formatFileSize(coverImage.size) : ''

  return (
    <div className="calls-modal__attachment">
      <div className="calls-modal__attachment-info">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            className="calls-modal__attachment-thumb calls-modal__attachment-thumb--image"
          />
        ) : (
          <span className="calls-modal__attachment-thumb" aria-hidden="true">
            IMG
          </span>
        )}
        <div className="calls-modal__attachment-text">
          <p className="calls-modal__attachment-name">{name}</p>
          {sizeLabel ? (
            <p className="calls-modal__attachment-size">{sizeLabel}</p>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        className="calls-modal__attachment-remove"
        aria-label="Remove image"
        onClick={onRemove}
      >
        <img src={ICONS.callRemoveX} alt="" width={10} height={10} />
      </button>
    </div>
  )
}

const appendArray = (formData, key, values = []) => {
  values.forEach(value => {
    if (value == null || value === '') return
    formData.append(`${key}[]`, value)
  })
}

const buildCreateFormData = (values, formType, { isEditing = false } = {}) => {
  const isFormal = formType === 'formal'
  const isGlobal = isFormal && values.scope === 'global'
  const addressTypes = isFormal
    ? isGlobal
      ? []
      : values.cities || []
    : values.respondents || []

  const formData = new FormData()
  formData.append('type', formType === 'formal' ? 'formal' : 'informal')
  formData.append('title', String(values.title || '').trim())
  formData.append('details', String(values.details || '').trim())
  formData.append('open_for_days', String(Number(values.duration) || 10))
  formData.append(
    'response_limit',
    String(Number(values.limit) || (isFormal ? 100 : 10))
  )
  formData.append('is_global', isGlobal ? '1' : '0')

  appendArray(
    formData,
    'project_ids',
    values.project?.id ? [values.project.id] : []
  )
  appendArray(formData, 'category_ids', values.categories || [])
  appendArray(formData, 'address_types', addressTypes)

  const existingMediaIds = Array.isArray(values.existingMediaIds)
    ? values.existingMediaIds.filter(Boolean)
    : []

  if (values.coverImage instanceof File) {
    formData.append('medias[]', values.coverImage)
    if (isEditing) {
      appendArray(formData, 'remove_media_ids', existingMediaIds)
    }
  } else if (isEditing && !values.coverImage) {
    appendArray(formData, 'remove_media_ids', existingMediaIds)
  }

  return formData
}

/**
 * CreateCallModal
 * ------------------------------------------------------------------
 * Choice step + Formik Informal/Formal form → POST /api/v1/calls.
 */
const getUserAddressTypes = (user = null) => {
  const addresses = Array.isArray(user?.addresses) ? user.addresses : []
  const userLevel = getUserCityHubLevel(user)
  const types = new Set()

  addresses.forEach(address => {
    // Only address slots that have an assigned city (hide unassigned tiers).
    if (!address?.city && !address?.city_id) return
    const type = String(address?.address_type || '')
      .trim()
      .toLowerCase()
    if (!ADDRESS_TYPE_OPTIONS.some(option => option.value === type)) return
    // Same unlock rules as City Hub / Create Project: Primary L1, Secondary L2, Tertiary L3.
    if (!isCityTierAccessible(type, userLevel)) return
    types.add(type)
  })

  return ADDRESS_TYPE_OPTIONS.filter(option => types.has(option.value)).map(
    option => {
      const cityName = formatCityName(getAddressByType(addresses, option.value))
      const hasCityName = cityName && cityName !== '--'

      return {
        ...option,
        label: hasCityName ? `${option.label} — ${cityName}` : option.label,
      }
    },
  )
}

const CreateCallModal = ({
  isOpen,
  onClose,
  onSubmit,
  editCall = null,
  defaultCityTypes: defaultCityTypesProp = null,
  allowedCityTypes: allowedCityTypesProp = null,
}) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)
  const [step, setStep] = useState('choice')
  const fileInputRef = useRef(null)
  const { categories: categoryOptions } = useCategories()
  const allCityOptions = useMemo(
    () => getUserAddressTypes(currentUser),
    [currentUser]
  )
  const cityOptions = useMemo(() => {
    if (!Array.isArray(allowedCityTypesProp) || !allowedCityTypesProp.length) {
      return allCityOptions
    }
    const allowed = new Set(
      allowedCityTypesProp
        .map(type => String(type || '').trim().toLowerCase())
        .filter(Boolean)
    )
    return allCityOptions.filter(option => allowed.has(option.value))
  }, [allCityOptions, allowedCityTypesProp])
  const defaultCityTypes = useMemo(() => {
    const available = cityOptions.map(option => option.value)
    const availableSet = new Set(available)

    const preferredSource =
      Array.isArray(defaultCityTypesProp) && defaultCityTypesProp.length
        ? defaultCityTypesProp
        : Array.isArray(allowedCityTypesProp) && allowedCityTypesProp.length
          ? allowedCityTypesProp
          : null

    if (preferredSource) {
      const preferred = preferredSource
        .map(type => String(type || '').trim().toLowerCase())
        .filter(type => availableSet.has(type))
      if (preferred.length) return preferred
    }

    if (available.includes('primary')) return ['primary']
    return available.slice(0, 1)
  }, [allowedCityTypesProp, cityOptions, defaultCityTypesProp])
  const isEditing = Boolean(editCall?.id)
  const editType =
    String(editCall?.type || editCall?.raw?.type || '')
      .trim()
      .toLowerCase() === 'formal'
      ? 'formal'
      : 'informal'

  const handleClose = useCallback(() => {
    setStep('choice')
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return undefined
    if (isEditing) {
      setStep(editType)
    } else {
      setStep('choice')
    }
    return undefined
  }, [isOpen, isEditing, editType])

  useEffect(() => {
    if (!isOpen) return undefined
    let cancelled = false

    const loadAddresses = async () => {
      const response = await get(endpoints.getMe, {
        params: { with: 'addresses.city' },
      })
      if (!cancelled && response?.data?.success) {
        dispatch(setUser(response.data.result))
      }
    }

    loadAddresses()
    return () => {
      cancelled = true
    }
  }, [isOpen, dispatch])

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = buildCreateFormData(values, step, { isEditing })
      const response = isEditing
        ? await patch(endpoints.updateCall(editCall.id), formData)
        : await post(endpoints.createCall, formData)

      if (!response?.data?.success) {
        toastApiError(
          response,
          isEditing ? 'Failed to update call' : 'Failed to create call'
        )
        return
      }

      toast.success(
        response?.data?.message ||
          (isEditing
            ? 'Call updated successfully'
            : step === 'formal'
              ? 'Call submitted for review'
              : 'Call published successfully'),
        TOAST_OPTIONS
      )

      if (onSubmit) {
        await onSubmit({
          ...values,
          type: step,
          result: response.data.result,
          isEditing,
        })
      }

      resetForm()
      handleClose()
    } catch (err) {
      toastApiError(
        err,
        isEditing ? 'Failed to update call' : 'Failed to create call'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const acceptImageFile = (
    file,
    setFieldValue,
    setFieldError,
    setFieldTouched
  ) => {
    if (!file) {
      setFieldValue('coverImage', null, false)
      return
    }

    const mime = String(file.type || '').toLowerCase()
    const ext = String(file.name || '')
      .split('.')
      .pop()
      ?.toLowerCase()
    const isImage =
      mime === 'image/png' ||
      mime === 'image/jpeg' ||
      mime === 'image/jpg' ||
      ['png', 'jpg', 'jpeg'].includes(ext)

    if (!isImage) {
      // Keep validate=false so Formik doesn't wipe this manual error.
      setFieldValue('coverImage', null, false)
      setFieldTouched('coverImage', true, false)
      setFieldError('coverImage', 'Only PNG or JPG files are allowed')
      return
    }

    setFieldValue('coverImage', file, false)
    setFieldTouched('coverImage', true, false)
    setFieldError('coverImage', undefined)
  }

  const renderChoice = () => (
    <div className="calls-modal calls-modal--choice">
      <div className="calls-modal__header">
        <h3 className="calls-modal__title">Create Inbox</h3>
        <p className="calls-modal__subtitle">
          Is this a casual ask to the community, or a formal &quot;Call for
          Artists&quot;?
        </p>
      </div>

      <div className="calls-modal__options">
        <button
          type="button"
          className="calls-modal__option calls-modal__option--informal"
          onClick={() => setStep('informal')}
        >
          <span className="calls-modal__option-main">
            <span className="calls-modal__option-icon" aria-hidden="true">
              <img
                src={ICONS.callInformalMessages}
                alt=""
                width={24}
                height={24}
              />
            </span>
            <span className="calls-modal__option-body">
              <span className="calls-modal__option-title">Informal Inbox</span>
              <span className="calls-modal__option-desc">
                Looking for bands to share a July date with!
              </span>
            </span>
          </span>
          <img
            src={ICONS.callArrowRightOrange}
            alt=""
            className="calls-modal__option-arrow"
            width={24}
            height={24}
          />
        </button>

        <button
          type="button"
          className="calls-modal__option calls-modal__option--formal"
          onClick={() => setStep('formal')}
        >
          <span className="calls-modal__option-main">
            <span className="calls-modal__option-icon" aria-hidden="true">
              <img
                src={ICONS.callFormalSearchStatus}
                alt=""
                width={24}
                height={24}
              />
            </span>
            <span className="calls-modal__option-body">
              <span className="calls-modal__option-title">
                Formal Call for Artists
              </span>
              <span className="calls-modal__option-desc">
                Toronto Music &amp; Arts Festival Call for Artists
              </span>
            </span>
          </span>
          <img
            src={ICONS.callArrowRightGreen}
            alt=""
            className="calls-modal__option-arrow"
            width={24}
            height={24}
          />
        </button>

        <div className="calls-modal__note" role="note">
          <span className="calls-modal__note-icon" aria-hidden="true">
            <img src={ICONS.callInfoCircle} alt="" width={24} height={24} />
          </span>
          <p className="calls-modal__note-text">
            Informal Inboxes are posted immediately. Avoid self-promotion and
            you&apos;ll be golden.
            <br />
            Formal Calls for Artists are reviewed before publishing.
          </p>
        </div>
      </div>
    </div>
  )

  const renderForm = () => {
    const copy = STEP_COPY[step] || STEP_COPY.informal
    const isFormal = step === 'formal'
    const title = isEditing ? copy.editTitle : copy.title
    const submitLabel = isEditing ? copy.editSubmitLabel : copy.submitLabel
    const limitOptions = getLimitOptions(step)

    return (
      <Formik
        key={`${step}-${editCall?.id || 'new'}-${isOpen ? 'open' : 'closed'}`}
        enableReinitialize
        initialValues={
          isEditing
            ? callToFormValues(editCall, step)
            : {
                ...INITIAL_VALUES,
                respondents: defaultCityTypes,
                cities: defaultCityTypes,
              }
        }
        validationSchema={buildValidationSchema(step)}
        onSubmit={handleFormSubmit}
        validateOnBlur
        validateOnChange
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          setFieldTouched,
          setFieldError,
          isSubmitting,
        }) => (
          <Form className="calls-modal calls-modal--form" noValidate>
            <div className="calls-modal__main">
              <div className="calls-modal__header">
                <h3 className="calls-modal__title">{title}</h3>
                <p className="calls-modal__subtitle">{copy.subtitle}</p>
              </div>

              <div className="calls-modal__fields">
                <ProjectSelect
                  label="What project of yours is this associated with?"
                  required
                  placeholder="Select a project"
                  value={values.project}
                  onChange={next => {
                    setFieldValue('project', next)
                    setFieldTouched('project', true, false)
                  }}
                  onBlur={() => setFieldTouched('project', true)}
                  error={
                    touched.project && errors.project
                      ? typeof errors.project === 'string'
                        ? errors.project
                        : 'Project is required'
                      : undefined
                  }
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                />

                <CategoriesMultiselect
                  className="calls-modal__chips-field"
                  label="What categories should this inbox show up under?"
                  name="categories"
                  required
                  options={categoryOptions}
                  selected={values.categories}
                  checkIconSrc={ICONS.check}
                  onChange={next => {
                    setFieldValue('categories', next)
                    setFieldTouched('categories', true, false)
                  }}
                  error={touched.categories && errors.categories}
                />

                <CustomInput
                  label="Title"
                  name="title"
                  required
                  maxLength={TITLE_MAX_LENGTH}
                  placeholder={copy.titlePlaceholder}
                  value={values.title}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched('title', true)}
                  error={touched.title && errors.title}
                />

                <CustomInput
                  label="Details"
                  name="details"
                  textarea
                  rows={3}
                  required
                  placeholder={copy.detailsPlaceholder}
                  value={values.details}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched('details', true)}
                  error={touched.details && errors.details}
                />

                <CategoriesMultiselect
                  className="calls-modal__chips-field calls-modal__chips-field--duration"
                  label="How long should this inbox stay open?"
                  name="duration"
                  required
                  options={DURATION_OPTIONS}
                  selected={values.duration ? [values.duration] : []}
                  checkIconSrc={ICONS.check}
                  onChange={next => {
                    const nextValue = next.find(
                      value => value !== values.duration
                    )
                    if (nextValue) {
                      setFieldValue('duration', nextValue)
                      setFieldTouched('duration', true, false)
                    }
                  }}
                  error={touched.duration && errors.duration}
                />

                {isFormal ? (
                  <div className="calls-modal__scope">
                    <CategoriesMultiselect
                      className="calls-modal__chips-field calls-modal__chips-field--scope"
                      label="Is this a Global Call or just for specific cities you work in?"
                      name="scope"
                      required
                      options={SCOPE_OPTIONS}
                      selected={values.scope ? [values.scope] : []}
                      checkIconSrc={ICONS.check}
                      onChange={next => {
                        const nextValue = next.find(
                          value => value !== values.scope
                        )
                        if (nextValue) {
                          setFieldValue('scope', nextValue)
                          setFieldTouched('scope', true, false)
                          if (nextValue === 'global') {
                            setFieldValue('cities', [])
                          } else if (!values.cities.length) {
                            setFieldValue('cities', defaultCityTypes)
                          }
                        }
                      }}
                      error={touched.scope && errors.scope}
                    />

                    {values.scope === 'specific' && (
                      <div className="calls-modal__cities-panel">
                        <CategoriesMultiselect
                          className="calls-modal__chips-field calls-modal__chips-field--cities"
                          label="What cities should this call be open to submissions from?"
                          name="cities"
                          required
                          options={cityOptions}
                          selected={values.cities.filter(value =>
                            cityOptions.some(option => option.value === value)
                          )}
                          checkIconSrc={ICONS.check}
                          onChange={next => {
                            setFieldValue('cities', next)
                            setFieldTouched('cities', true, false)
                          }}
                          error={touched.cities && errors.cities}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <CategoriesMultiselect
                    className="calls-modal__chips-field calls-modal__chips-field--respondents"
                    label="Who can respond?"
                    helperText="(Select all that apply)"
                    name="respondents"
                    required
                    options={cityOptions}
                    selected={values.respondents.filter(value =>
                      cityOptions.some(option => option.value === value)
                    )}
                    checkIconSrc={ICONS.check}
                    onChange={next => {
                      setFieldValue('respondents', next)
                      setFieldTouched('respondents', true, false)
                    }}
                    error={touched.respondents && errors.respondents}
                  />
                )}

                <div
                  className={`amok-field ${
                    touched.limit && errors.limit ? 'amok-field--error' : ''
                  }`}
                >
                  <label className="amok-field__label" htmlFor="calls-limit">
                    Limit of responses
                    <span className="amok-field__required"> *</span>
                  </label>
                  <Select
                    inputId="calls-limit"
                    className={`amok-role-select ${
                      touched.limit && errors.limit
                        ? 'amok-role-select--error'
                        : ''
                    }`}
                    classNamePrefix="amok-role-select"
                    options={limitOptions}
                    value={
                      limitOptions.find(
                        option => option.value === values.limit
                      ) || null
                    }
                    onChange={option => {
                      setFieldValue('limit', option ? option.value : '')
                      setFieldTouched('limit', true, false)
                    }}
                    onBlur={() => setFieldTouched('limit', true)}
                    placeholder="Select limit"
                    isSearchable={false}
                    isClearable={false}
                    openMenuOnFocus
                    openMenuOnClick
                    components={{
                      DropdownIndicator: ChevronDropdown,
                      IndicatorSeparator: null,
                    }}
                    menuPortalTarget={
                      typeof document !== 'undefined' ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={{
                      ...customStyles(!!(touched.limit && errors.limit)),
                      menuPortal: base => ({ ...base, zIndex: 2000 }),
                    }}
                  />
                  {touched.limit && errors.limit && (
                    <p className="amok-field__message amok-field__message--error">
                      {errors.limit}
                    </p>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="calls-modal__upload-input"
                  onChange={event => {
                    const file = event.target.files?.[0]
                    acceptImageFile(
                      file,
                      setFieldValue,
                      setFieldError,
                      setFieldTouched
                    )
                    event.target.value = ''
                  }}
                />

                {values.coverImage ? (
                  <CoverAttachment
                    coverImage={values.coverImage}
                    onRemove={() => {
                      setFieldValue('coverImage', null)
                      setFieldError('coverImage', undefined)
                    }}
                  />
                ) : (
                  <div
                    className={`calls-modal__upload ${
                      errors.coverImage ? 'calls-modal__upload--error' : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        fileInputRef.current?.click()
                      }
                    }}
                    onDragOver={event => {
                      event.preventDefault()
                      event.stopPropagation()
                    }}
                    onDrop={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      const file = event.dataTransfer?.files?.[0]
                      acceptImageFile(
                        file,
                        setFieldValue,
                        setFieldError,
                        setFieldTouched
                      )
                    }}
                  >
                    <span
                      className="calls-modal__upload-icon"
                      aria-hidden="true"
                    >
                      <img
                        src={ICONS.callUploadExport}
                        alt=""
                        width={16}
                        height={16}
                      />
                    </span>
                    <p className="calls-modal__upload-title">
                      Click to upload or drag and drop
                    </p>
                    <p className="calls-modal__upload-hint">
                      PNG, JPG
                    </p>
                    {errors.coverImage && (
                      <p className="amok-field__message amok-field__message--error">
                        {errors.coverImage}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="calls-modal__actions">
              <CustomButton
                variant="danger"
                size="md"
                type="button"
                className="calls-modal__cancel"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="submit"
                className="calls-modal__submit"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {submitLabel}
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      className={`amok-modal amok-modal--calls${
        step === 'choice'
          ? ' amok-modal--calls-choice'
          : ' amok-modal--calls-form'
      }`}
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
      onClosed={() => setStep('choice')}
    >
      <ModalBody className="amok-modal__body calls-modal__body">
        {step === 'choice' && !isEditing ? renderChoice() : renderForm()}
      </ModalBody>
    </Modal>
  )
}

CreateCallModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  editCall: PropTypes.object,
  /** When set, pre-select these address types in city chips. */
  defaultCityTypes: PropTypes.arrayOf(PropTypes.string),
  /** When set (e.g. City Hub), only these address types appear in the selector. */
  allowedCityTypes: PropTypes.arrayOf(PropTypes.string),
}

export default CreateCallModal
